/**
 * What the new-collection wizard needs before it will let somebody move on.
 *
 * These were a `switch` inside Collections.js with nothing testing them, and
 * they are not arbitrary form validation -- each is a rule about what makes a
 * collection worth keeping once the person is back from the field and cannot
 * go and look again. So they are here, as functions of their inputs, and each
 * is tested.
 */

//: The four standard views. Each is optional (2026-10-06): a bracket or crust
//: fungus has no stem or gills, and a damaged specimen may have lost them, so
//: requiring them made people upload stand-in photos that misdescribe it.
export const STANDARD_PHOTOS = [
  { type: 'habitat', labelKey: 'step5.photo1', hintKey: 'step5.photo1Hint' },
  { type: 'cap_top', labelKey: 'step5.photo2', hintKey: 'step5.photo2Hint' },
  { type: 'fertile_layer', labelKey: 'step5.photo3', hintKey: 'step5.photo3Hint' },
  { type: 'stipe', labelKey: 'step5.photo4', hintKey: 'step5.photo4Hint' },
];

export const OPTIONAL_PHOTOS = [
  { type: 'cross_section', labelKey: 'step5.photo5' },
  { type: 'other', labelKey: 'step5.other' },
];

/** `datetime-local` wants local wall-clock time, which toISOString is not. */
export const toLocalDatetime = (d = new Date()) =>
  new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

export const EMPTY_NEW = {
  qr_uid: '', collection_datetime: '',
  substrate: '', fruiting_body_size: '', phenological_stage: '',
  fruiting_body_count: '', growth_pattern: '', fruiting_body_size_estimated: false,
  gps_lat: '', gps_lng: '',
  current_taxon_name: '', current_rank: '', current_confidence: '',
  general_notes: '', region: '', colour_change: null,
  // Global specimens + habitat (migration 0011). country_code is uppercased
  // server-side, so the field accepts either case.
  country_code: '', locality_text: '',
  host_species: '', microenvironment: '', habitat_biome: '',
};

/**
 * May the wizard leave `step`?
 *
 *   1  a code, because the code is how the specimen is found again
 *   2  substrate, some vegetation, and SOMEWHERE -- a fix or a named region
 *   3  the four morphology answers that cannot be recovered from a photograph
 *   4  a name is optional, but a TYPED name must have been chosen from the
 *      dictionary, so a typo cannot become a taxon
 *   5  nothing: every photograph is optional, though one marked "Other"
 *      needs a word about what it shows
 */
export function canAdvance(step, {
  form = {},
  vegetation = [],
  regionSelected = false,
  taxonQuery = '',
  taxonSelected = false,
  photos = {},
  otherPhotoNote = '',
} = {}) {
  switch (step) {
    case 1:
      return (form.qr_uid || '').trim() !== '';
    case 2:
      return form.substrate !== '' && form.substrate !== undefined
        && vegetation.length >= 1
        // A coordinate or a region: a record with neither cannot be put on a
        // map or counted in a survey, and neither can be added later.
        && (form.gps_lat !== '' || regionSelected);
    case 3:
      return form.fruiting_body_size !== '' && form.phenological_stage !== ''
        && form.fruiting_body_count !== '' && form.growth_pattern !== '';
    case 4:
      return !taxonQuery.trim() || taxonSelected;
    case 5:
      // No photograph is required, not even the cut face after a colour
      // change or a photograph of an unnamed plant: both are offered, and
      // Step 5 says why they help. "Other" is only meaningful with a word
      // about what it is.
      return !(photos.other && !otherPhotoNote.trim());
    default:
      return true;
  }
}
